import { useState } from 'react';
import { Badge, Button, ConfirmModal, Modal } from '@safetyhub/ui';
import { formatPhone, MemberDedupContactMatchedBy } from '@safetyhub/shared';
import { memberDedupApi, MyDuplicateCandidate } from '../lib/memberDedup';
import { extractApiError } from '../lib/organizations';
import { useToastStore } from '../stores/toast';
import { useActionItemsStore } from '../stores/actionItems';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from './modalLayers';

/** 일치 근거 라벨 — 본인 축은 연락처 축만(타입이 강제, 이름 단독 축은 서버가 제시하지 않음) */
const MATCHED_BY_LABEL: Record<MemberDedupContactMatchedBy, string> = {
  phone: '전화번호가 회원님과 일치',
  email: '이메일이 회원님과 일치',
};

/**
 * 내 중복 인원 판정 모달 (P5 당사자 본인 병합).
 *
 * 대표 1차 판정이 스킵한 분(계정 연결 행은 대표가 흡수할 수 없다)은 당사자가 최종 판정한다 —
 * 내 계정과 신원이 겹치는 무연결 인원을 여기서 병합하거나 「내가 아님」으로 처리한다
 * (결정 2026-08-14 §4). 판정 화면에 싣는 것은 신원 필드 + 등록 회사 이름(내 소속 범위)뿐이다.
 *
 * ⚠️ 2026-09-29(`banner-slot-policy` P4)에 **배너에서 분리**했다 — 안내는 「확인이 필요한 항목」
 * 으로 내려갔지만(결정 기록 1항) **이 판정 화면이 그 기능의 본체**라 그대로 살아 있다.
 * 열림 상태를 store가 소유하는 이유는 바·벨 **두 표면**이 같은 모달을 열기 때문이고, 그래서
 * 이 컴포넌트는 Layout에 **한 번만** 마운트된다.
 */
export default function MyDuplicateModal() {
  const { showToast } = useToastStore();
  const candidates = useActionItemsStore((s) => s.duplicates);
  const open = useActionItemsStore((s) => s.duplicateModalOpen);
  const closeModal = useActionItemsStore((s) => s.closeDuplicateModal);
  const removeDuplicate = useActionItemsStore((s) => s.removeDuplicate);
  const [confirmTarget, setConfirmTarget] = useState<MyDuplicateCandidate | null>(null);
  const [busyMemberId, setBusyMemberId] = useState<string | null>(null);

  const handleMerge = async (candidate: MyDuplicateCandidate) => {
    setBusyMemberId(candidate.memberId);
    try {
      await memberDedupApi.mergeIntoMe(candidate.memberId);
      showToast('내 정보로 병합했습니다.', 'success');
      removeDuplicate(candidate.memberId);
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setBusyMemberId(null);
      setConfirmTarget(null);
    }
  };

  /**
   * 「내가 아님」 — 이 항목의 **「확인했음」에 해당하는 경로**다(결정 기록 개정 2). 후보마다
   * 판정이 갈릴 수 있어 항목 단위로 한꺼번에 내릴 수 없으므로 항목에 `acknowledge`를 두지 않고
   * 여기 후보별로 둔다.
   */
  const handleDismissCandidate = async (candidate: MyDuplicateCandidate) => {
    setBusyMemberId(candidate.memberId);
    try {
      await memberDedupApi.dismissMyDuplicate(candidate.memberId);
      showToast('내가 아닌 것으로 처리했습니다.', 'success');
      removeDuplicate(candidate.memberId);
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setBusyMemberId(null);
    }
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open={open}
        onClose={closeModal}
        title="내 중복 인원 정보 확인"
        size="lg"
        overlayZClassName={MODAL_LAYER.root}
      >
        <p className="text-sm text-ink-500 mb-4">
          아래 인원 정보는 회원님 소속 회사에 등록된 인원 중 연락처(전화 또는 이메일)가
          회원님과 일치하는 건입니다. 본인이 맞으면 내 정보로 병합해 하나로 관리하고, 다른
          사람이면 &lsquo;내가 아님&rsquo;으로 처리하세요.
        </p>
        <div className="space-y-3">
          {candidates.map((candidate) => {
            const busy = busyMemberId === candidate.memberId;
            return (
              <div key={candidate.memberId} className="rounded-card border border-ink-200 p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-ink-900">{candidate.name}</span>
                  <Badge tone="blue">{MATCHED_BY_LABEL[candidate.matchedBy]}</Badge>
                </div>
                <p className="mt-1 text-sm text-ink-600">
                  {candidate.phone ? formatPhone(candidate.phone) : '전화번호 없음'} ·{' '}
                  {candidate.email ?? '이메일 없음'}
                </p>
                <p className="mt-0.5 text-xs text-ink-500">
                  등록된 회사: {candidate.companyNames.join(', ')}
                </p>
                {!candidate.hasOrganizationRole && (
                  <p className="mt-0.5 text-xs text-ink-400">
                    현장 담당자·외부 연락처로 등록된 인원이라 멤버 관리 목록에는 나타나지
                    않습니다. 현장 상세의 담당자 화면에서 관리됩니다.
                  </p>
                )}
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => void handleDismissCandidate(candidate)}
                  >
                    내가 아님
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirmTarget(candidate)}
                  >
                    내 정보로 병합
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Modal>

      <ConfirmModal
        open={confirmTarget !== null}
        title="내 정보로 병합"
        body={
          confirmTarget
            ? `'${confirmTarget.name}' 인원 정보를 회원님 계정으로 병합합니다. 해당 인원의 소속·기록 연결이 회원님으로 통합됩니다.`
            : ''
        }
        confirmLabel="병합"
        busy={busyMemberId !== null}
        overlayZClassName={MODAL_LAYER.sub}
        overlayBgClassName={SUB_MODAL_OVERLAY_BG}
        onConfirm={() => {
          if (confirmTarget) void handleMerge(confirmTarget);
        }}
        onClose={() => setConfirmTarget(null)}
      />
    </>
  );
}
